import { SelectProps } from "@amzn/awsui-components-react";
import { useMemo } from "react";
import { buildingsData } from "data";
import { buildingIdFormat, buildingResponse } from "interfaces";
import { CheckboxNode } from "common/component/FilterSection/CheckboxFilter/CheckboxFilter";

/**
 * Custom hook that transforms buildings data into select options format
 *
 * @returns {SelectProps.Options} Array of select options with building names as labels and values
 */
export const useBuildingsForSelect = (): SelectProps.Options => {
    const buildings: buildingResponse = buildingsData;

    return buildings.map((building) => {
        return { label: building.building, value: JSON.stringify(building) };
    });
};

/**
 * Custom hook that builds CheckboxNode structure with parent-child relationships
 * based on the buildings hierarchy
 *
 * @returns {CheckboxNode[]} Array of CheckboxNode with parent references
 */
export const useBuildingsWithParentChild = (): CheckboxNode[] => {
    return useMemo(() => {
        const buildings: buildingResponse = buildingsData;
        const hierarchy: Record<string, Record<string, Record<string, string[]>>> = {};

        buildings.forEach((building) => {
            if (!hierarchy[building.region]) {
                hierarchy[building.region] = {};
            }
            if (!hierarchy[building.region][building.country]) {
                hierarchy[building.region][building.country] = {};
            }
            if (!hierarchy[building.region][building.country][building.city]) {
                hierarchy[building.region][building.country][building.city] = [];
            }
            hierarchy[building.region][building.country][building.city].push(building.building);
        });

        const result: CheckboxNode[] = [];

        Object.keys(hierarchy)
            .sort()
            .forEach((region) => {
                const regionNode: CheckboxNode = {
                    id: encodeURIComponent(
                        JSON.stringify({ name: region, type: "region", root: "" } as buildingIdFormat),
                    ),
                    name: region,
                    children: [],
                };

                Object.keys(hierarchy[region])
                    .sort()
                    .forEach((country) => {
                        const countryNode: CheckboxNode = {
                            id: encodeURIComponent(
                                JSON.stringify({ name: country, type: "country", root: region } as buildingIdFormat),
                            ),
                            name: country,
                            parent: regionNode,
                            children: [],
                        };

                        Object.keys(hierarchy[region][country])
                            .sort()
                            .forEach((city) => {
                                const cityNode: CheckboxNode = {
                                    id: encodeURIComponent(
                                        JSON.stringify({
                                            name: city,
                                            type: "city",
                                            root: `${region}/${country}`,
                                        } as buildingIdFormat),
                                    ),
                                    name: city,
                                    parent: countryNode,
                                    children: [],
                                };
                                
                                hierarchy[region][country][city].sort().forEach((building) => {
                                    const buildingNode: CheckboxNode = {
                                        id: encodeURIComponent(
                                            JSON.stringify({
                                                name: building,
                                                type: "building",
                                                root: `${region}/${country}/${city}`,
                                            } as buildingIdFormat),
                                        ),
                                        name: building,
                                        parent: cityNode,
                                    };
                                    cityNode.children!.push(buildingNode);
                                });

                                countryNode.children!.push(cityNode);
                            });

                        regionNode.children!.push(countryNode);
                    });

                result.push(regionNode);
            });

        return result;
    }, []);
};
